import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { API_BASE_URL } from '../services/api';

const PermissionsContext = createContext();

export const MODULES = {
  DASHBOARD: {
    name: 'dashboard',
    submodules: {
      TOTAL_WORKERS: 'total_workers',
      PRESENT_TODAY: 'present_today',
      ABSENT_TODAY: 'absent_today',
      ON_LEAVE: 'on_leave',
      LATE_ARRIVALS: 'late_arrivals',
      OVERTIME_HOURS: 'overtime_hours',
      PENDING_LEAVES: 'pending_leaves',
      MONTHLY_ATTENDANCE: 'monthly_attendance',
      DEPARTMENTS: 'departments',
      RECENT_WORKERS: 'recent_workers',
      PENDING_LEAVE_REQUESTS: 'pending_leave_requests'
    }
  },
  WORKERS: {
    name: 'workers',
    submodules: {
      WORKER_LIST: 'worker_list',
      WORKER_PROFILE: 'worker_profile',
      WORKER_DOCUMENTS: 'worker_documents',
      PROCESS_PRICING: 'process_pricing',
      STONE_PRICING: 'stone_pricing',
      JEWELRY_PRICING: 'jewelry_pricing',
      DIAMOND_PRICING: 'diamond_pricing',
      SALARY_INFO: 'salary_info',
      BANK_DETAILS: 'bank_details'
    }
  },
  ATTENDANCE: {
    name: 'attendance',
    submodules: {
      DAILY_ATTENDANCE: 'daily_attendance',
      ATTENDANCE_REPORTS: 'attendance_reports',
      ATTENDANCE_SUMMARY: 'attendance_summary'
    }
  },
  PAYROLL: {
    name: 'payroll',
    submodules: {
      SALARY_CALCULATION: 'salary_calculation',
      PAYROLL_REPORTS: 'payroll_reports'
    }
  },
  LEAVE: {
    name: 'leave',
    submodules: {
      LEAVE_APPLICATIONS: 'leave_applications',
      LEAVE_APPROVALS: 'leave_approvals',
      LEAVE_REPORTS: 'leave_reports'
    }
  },
  LEAVE_TYPES: {
    name: 'leave_types',
    submodules: {
      LEAVE_TYPE_MASTER: 'leave_type_master'
    }
  },
  HOLIDAYS: {
    name: 'holidays',
    submodules: {
      HOLIDAY_MASTER: 'holiday_master'
    }
  },
  SHIFTS: {
    name: 'shifts',
    submodules: {
      SHIFT_MASTER: 'shift_master'
    }
  },
  REPORTS: {
    name: 'reports',
    submodules: {
      ATTENDANCE_REPORTS: 'attendance_reports',
      PAYROLL_REPORTS: 'payroll_reports',
      LEAVE_REPORTS: 'leave_reports'
    }
  },
  USER_MANAGEMENT: {
    name: 'user_management',
    submodules: {
      USER_LIST: 'user_list',
      ROLE_MANAGEMENT: 'role_management',
      PERMISSIONS: 'permissions'
    }
  }
};

export const ACTIONS = {
  LIST: 'list',
  INFO: 'info',
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  REJECT: 'reject',
  EXPORT: 'export',
  VIEW_SALARY: 'view_salary'
};

export function PermissionsProvider({ children }) {
  const [userRole, setUserRole] = useState('employee');
  const [userPermissions, setUserPermissions] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyTokenAndLoadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyTokenAndLoadUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL }/auth/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        await loadUserPermissions(userData);
      } else {
        localStorage.removeItem('token');
        setLoading(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
      setLoading(false);
    }
  };

  const loadUserPermissions = async (user = currentUser) => {
    if (!user) return;
    
    try {
      setUserRole(user.role || 'employee');
      
      // Grant all permissions to admin user
      if (user.username === 'admin' || user.role === 'admin') {
        const allPermissions = {
          dashboard: {
            total_workers: ['list', 'view'],
            present_today: ['list', 'view'],
            absent_today: ['list', 'view'],
            on_leave: ['list', 'view'],
            late_arrivals: ['list', 'view'],
            overtime_hours: ['list', 'view'],
            pending_leaves: ['list', 'view'],
            monthly_attendance: ['list', 'view']
          },
          workers: {
            worker_list: ['list', 'view', 'create', 'edit', 'delete'],
            worker_profile: ['list', 'view', 'create', 'edit'],
            worker_documents: ['list', 'view', 'create', 'edit'],
            process_pricing: ['list', 'view', 'create', 'edit'],
            stone_pricing: ['list', 'view', 'create', 'edit'],
            jewelry_pricing: ['list', 'view', 'create', 'edit'],
            diamond_pricing: ['list', 'view', 'create', 'edit'],
            salary_info: ['list', 'view', 'create', 'edit', 'view_salary'],
            bank_details: ['list', 'view', 'create', 'edit']
          },
          attendance: {
            daily_attendance: ['list', 'view', 'create', 'edit', 'delete'],
            attendance_reports: ['list', 'view', 'export'],
            attendance_summary: ['list', 'view']
          },
          payroll: {
            salary_calculation: ['list', 'view', 'create', 'edit'],
            payroll_reports: ['list', 'view', 'export']
          },
          leave: {
            leave_applications: ['list', 'view', 'create', 'edit'],
            leave_approvals: ['list', 'view', 'approve', 'reject'],
            leave_reports: ['list', 'view', 'export']
          },
          leave_types: {
            leave_type_master: ['list', 'view', 'create', 'edit', 'delete']
          },
          holidays: {
            holiday_master: ['list', 'view', 'create', 'edit', 'delete']
          },
          shifts: {
            shift_master: ['list', 'view', 'create', 'edit', 'delete']
          },
          reports: {
            attendance_reports: ['list', 'view', 'export'],
            payroll_reports: ['list', 'view', 'export'],
            leave_reports: ['list', 'view', 'export']
          },
          user_management: {
            user_list: ['list', 'view', 'create', 'edit', 'delete'],
            role_management: ['list', 'view', 'create', 'edit', 'delete'],
            permissions: ['list', 'view', 'create', 'edit']
          }
        };
        setUserPermissions(allPermissions);
        setLoading(false);
        return;
      }
      
      if (user.id) {
        let finalPermissions = {};
        
        if (user.role_id) {
          try {
            const rolePermissions = await api.roles.getPermissions(user.role_id);
            if (rolePermissions && rolePermissions.permissions) {
              finalPermissions = rolePermissions.permissions;
            }
          } catch (error) {
            // No role permissions found
          }
        }
        
        try {
          const userSpecificPermissions = await api.userPermissions.getByUserId(user.id);
          if (userSpecificPermissions && userSpecificPermissions.permissions) {
            finalPermissions = mergePermissions(finalPermissions, userSpecificPermissions.permissions);
          }
        } catch (error) {
          // No user-specific permissions found
        }
        
        setUserPermissions(finalPermissions);
      }
    } catch (error) {
      console.error('Error loading user permissions:', error);
      setUserPermissions({});
    } finally {
      setLoading(false);
    }
  };

  // Helper function to merge role and user permissions
  const mergePermissions = (rolePermissions, userPermissions) => {
    const merged = { ...rolePermissions };
    
    // Merge user-specific permissions with role permissions
    Object.keys(userPermissions).forEach(module => {
      if (!merged[module]) {
        merged[module] = {};
      }
      
      Object.keys(userPermissions[module]).forEach(submodule => {
        if (!merged[module][submodule]) {
          merged[module][submodule] = [];
        }
        
        // Combine actions from both role and user permissions
        const combinedActions = [...new Set([
          ...merged[module][submodule],
          ...userPermissions[module][submodule]
        ])];
        
        merged[module][submodule] = combinedActions;
      });
    });
    
    return merged;
  };

  const hasPermission = (module, submodule, action) => {
    
    // Admin users have all permissions
    if (currentUser && (currentUser.username === 'admin' || currentUser.role === 'admin')) {
      return true;
    }
    
    // Handle both old and new permission formats
    if (typeof module === 'object' && module.name) {
      // New format: module is an object with name property
      const moduleName = module.name;
      if (userPermissions[moduleName]) {
        if (typeof userPermissions[moduleName] === 'boolean') {
          return userPermissions[moduleName];
        }
        if (userPermissions[moduleName][submodule]) {
          return userPermissions[moduleName][submodule].includes(action);
        }
      }
    } else {
      // Old format: direct module name
      if (userPermissions[module]) {
        if (typeof userPermissions[module] === 'boolean') {
          return userPermissions[module];
        }
        if (userPermissions[module][submodule]) {
          return userPermissions[module][submodule].includes(action);
        }
      }
    }
    return false;
  };

  const canAccessModule = (module) => {    
    // Admin users have full access
    if (currentUser && (currentUser.username === 'admin' || currentUser.role === 'admin')) {
      return true;
    }
    
    // Handle both boolean and object permissions
    if (typeof userPermissions[module] === 'boolean') {
      return userPermissions[module];
    }
    if (userPermissions[module] && typeof userPermissions[module] === 'object') {
      // Check if any submodule has 'list' permission
      const hasListPermission = Object.keys(userPermissions[module]).some(submodule => 
        userPermissions[module][submodule].includes('list')
      );
      return hasListPermission;
    }
    return false;
  };

  const canAccessSubmodule = (module, submodule) => {
    if (userPermissions[module] && userPermissions[module][submodule]) {
      return userPermissions[module][submodule].includes('list');
    }
    return false;
  };

  const getUserPermissions = () => {
    return userPermissions;
  };

  const updateUserRole = (role) => {
    setUserRole(role);
    loadUserPermissions();
  };

  const updateUserPermissions = async (userId, permissions) => {
    try {
      await api.userPermissions.update(userId, { permissions });
      if (currentUser && currentUser.id === userId) {
        setUserPermissions(permissions);
      }
      return true;
    } catch (error) {
      console.error('Error updating user permissions:', error);
      return false;
    }
  };

  const refreshPermissions = async () => {
    await loadUserPermissions();
  };

  const setUser = (userData) => {
    setCurrentUser(userData);
    loadUserPermissions(userData);
  };

  return (
    <PermissionsContext.Provider value={{
      userRole,
      userPermissions,
      currentUser,
      loading,
      hasPermission,
      canAccessModule,
      canAccessSubmodule,
      getUserPermissions,
      updateUserRole,
      updateUserPermissions,
      loadUserPermissions,
      refreshPermissions,
      setUser,
      MODULES,
      ACTIONS
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
}